import React from 'react'
import { createContext } from 'lib/createContext'
import { Loading, Message } from '@workwhile/ui'
import { parseErrorMessage } from 'api'
import { usePaginationQueryState } from 'lib/nuqs/usePaginationQueryState'
import { useReviewFiltersQueryState } from '../hooks/useReviewFiltersQueryState'
import { useTimeRangeQueryState } from '../hooks/useTimeRangeQueryState'
import { Work } from 'api/typings'

type ReviewShiftsData = {
  items: Work[]
  totalRows: number
  totalPages: number
  currentPage: number
  pageSize: number
}

type ReviewShiftsContextState = {
  state: {
    data: ReviewShiftsData
    dataUpdatedAt: number
    isFetching: boolean
    isError: boolean
    error: string
    pagination: {
      pageIndex: number
      pageSize: number
    }
    filters: {
      workId: number | null
      shiftId: number | null
      companyId: number | null
      companyGroupId: number | null
      workerId: number | null
    }
    timeRangeFilters: {
      startDate?: Date
      endDate?: Date
    }
  }
  actions: {
    refetch: () => void
    setPagination: ReturnType<typeof usePaginationQueryState>[1]
    setFilters: ReturnType<typeof useReviewFiltersQueryState>[1]
    setTimeRangeFilters: ReturnType<typeof useTimeRangeQueryState>[1]
  }
}

const [Context, useReviewShiftsContextValue] =
  createContext<ReviewShiftsContextState>({
    name: 'ReviewShiftsContext',
  })

type ReviewShiftsProviderProps = {
  children: React.ReactNode
  initialWorkerId?: number
  useQueryHook: (options: {
    workId?: number
    shiftId?: number
    companyId?: number
    companyGroupId?: number
    workerId?: number
    page?: number
    pageSize?: number
    startDate?: Date
    endDate?: Date
  }) => {
    data:
      | {
          items: Work[]
          totalRows: number
          totalPages: number
          currentPage: number
          pageSize: number
        }
      | undefined
    isPending: boolean
    isError: boolean
    error: Error | null
    dataUpdatedAt: number
    refetch: () => void
    isFetching: boolean
  }
}

export const ReviewShiftsProvider = ({
  children,
  initialWorkerId,
  useQueryHook,
}: ReviewShiftsProviderProps) => {
  const [pagination, setPagination] = usePaginationQueryState({
    pageIndexParser: 'integer',
  })
  const [filters, setFilters] = useReviewFiltersQueryState()
  const [timeRangeFilters, setTimeRangeFilters] = useTimeRangeQueryState()

  // Override workerId filter if initialWorkerId is provided
  const effectiveFilters = React.useMemo(() => {
    if (initialWorkerId !== undefined) {
      return {
        ...filters,
        workerId: initialWorkerId,
      }
    }
    return filters
  }, [filters, initialWorkerId])

  const {
    data,
    isPending: isLoading,
    isError,
    error,
    dataUpdatedAt,
    refetch,
    isFetching,
  } = useQueryHook({
    workId: effectiveFilters.workId ?? undefined,
    shiftId: effectiveFilters.shiftId ?? undefined,
    companyId: effectiveFilters.companyId ?? undefined,
    companyGroupId: effectiveFilters.companyGroupId ?? undefined,
    workerId: effectiveFilters.workerId ?? undefined,
    page: pagination.pageIndex,
    pageSize: pagination.pageSize,
    startDate: timeRangeFilters.startDate,
    endDate: timeRangeFilters.endDate,
  })

  if (isLoading) {
    return <Loading />
  }

  if (isError) {
    return (
      <Message variant="error" m={4} borderRadius="small">
        Sorry, something went wrong. Please reach out to eng support with the
        URL of the page if this persists.
      </Message>
    )
  }

  const value = {
    state: {
      data: data ?? {
        items: [],
        totalRows: 0,
        totalPages: 0,
        currentPage: pagination.pageIndex,
        pageSize: pagination.pageSize,
      },
      dataUpdatedAt,
      isFetching,
      isError,
      error: parseErrorMessage(error),
      pagination,
      filters: effectiveFilters,
      timeRangeFilters,
    },
    actions: {
      refetch,
      setPagination,
      setFilters,
      setTimeRangeFilters,
    },
  }

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export { useReviewShiftsContextValue }
